import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import SEOMeta from "@/components/SEOMeta";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "react-router-dom";
import {
  Shield,
  Award,
  Users,
  Clock,
  Hammer,
  Zap,
  Wrench,
  Truck,
  Building,
  MapPin,
  Phone,
  Mail,
  CheckCircle,
} from "lucide-react";
import heroImage from "@/assets/hero-construction.jpg";
import residentialImage from "@/assets/residential-construction.jpg";
import heavyEquipmentImage from "@/assets/heavy-equipment.jpg";

const Index = () => {
  const faqData = {
    faqs: [
      {
        question: "Who is Blue Knight Contracting and Construction?",
        answer:
          "Blue Knight Contracting and Construction is a full-service Ontario general contractor providing residential, commercial, and industrial construction services across multiple cities including Toronto, Ottawa, Mississauga, Hamilton, London, Windsor, Kingston, and throughout Ontario.",
      },
      {
        question: "What construction services do you provide in Ontario?",
        answer:
          "We provide comprehensive construction services including general contracting, skilled trades (electricians, plumbers, carpenters, HVAC), heavy equipment and site work, specialty contracting, design and engineering support, and safety coordination across all of Ontario.",
      },
      {
        question: "Do you offer construction services in Toronto and the GTA?",
        answer:
          "Yes, Blue Knight Contracting provides full construction and contracting services in Toronto, Mississauga, Brampton, Vaughan, Markham, Scarborough, and throughout the Greater Toronto Area (GTA) for residential, commercial, and industrial projects.",
      },
      {
        question: "Are you licensed and insured for construction work in Ontario?",
        answer:
          "Yes, Blue Knight Contracting and Construction is fully licensed, bonded, and insured across Ontario with over 20 years of experience and comprehensive coverage for all our construction and contracting services.",
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOMeta
        title="Toronto & GTA General Contractor | Blue Knight Construction"
        description="EServing Toronto, GTA, Hamilton & Niagara Region. 20+ years delivering residential, commercial & industrial construction. Licensed & insured. Get a free quote."
        keywords="carpentry contractor Niagara, woodworking contractor Hamilton, GTA carpentry services, custom cabinetry, deck framing, fence installation, wood framing services, contracting & construction Niagara, residential carpentry GTA, professional carpentry Hamilton, carpentry, decks, fences, framing, cabinetry, residential, commercial, Toronto, Hamilton, GTA, Niagara Region"
        schemaType="LocalBusiness"
        schemaData={{
          name: "Blue Knight Contracting & Construction",
          description:
            "Ontario's trusted general contractor providing residential, commercial & industrial construction services",
          telephone: "(416) 555-BLUE",
          email: "info@blueknightcontracting.ca",
          foundingDate: "2004",
          yearsInOperation: "20+",
          numberOfEmployees: "50+",
          areaServed: ["Niagara Region", "Hamilton", "Toronto GTA"],
        }}
      />
      <SchemaMarkup type="FAQ" data={faqData} />
      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary to-primary-glow text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Professional construction site with Blue Knight Contracting team working on commercial building in Ontario"
            className="w-full h-full object-cover opacity-20"
            loading="eager"
            decoding="async"
            width="1335"
            height="751"
          />
        </div>
        <div className="relative container mx-auto px-4 py-20 lg:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              Trusted General Contracting & Construction Services
              <span className="block text-accent">in Ontario</span>
            </h1>
            <p className="text-xl lg:text-2xl mb-8 opacity-90 max-w-3xl mx-auto">
              Blue Knight Contracting and Construction is Ontario's trusted general contractor delivering exceptional
              quality with over 20 years of experience serving Toronto, Ottawa, Mississauga, Hamilton, London, Windsor,
              Kingston, and all of Ontario with residential, commercial, and industrial construction services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="hero" size="lg" className="text-lg px-8 py-4">
                <Phone className="mr-2 h-5 w-5" />
                Request Your Free Quote Today
              </Button>
              <Link to="/gallery">
                <Button variant="contact" size="lg" className="text-lg px-8 py-4">
                  View Service Gallery
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="flex flex-col items-center">
              <div className="bg-primary/10 p-4 rounded-full mb-4">
                <Award className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">20+ Years Experience</h3>
              <p className="text-foreground/80">Serving Ontario with excellence since 2004</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-primary/10 p-4 rounded-full mb-4">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Licensed & Insured</h3>
              <p className="text-foreground/80">Fully bonded and insured across all provinces</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-primary/10 p-4 rounded-full mb-4">
                <Users className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">500+ Projects</h3>
              <p className="text-foreground/80">Completed across residential, commercial & industrial</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-primary/10 p-4 rounded-full mb-4">
                <Clock className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">24/7 Emergency</h3>
              <p className="text-foreground/80">Available for urgent construction needs</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Comprehensive Construction & Contracting Services</h2>
            <p className="text-xl text-foreground/70 max-w-3xl mx-auto">
              Blue Knight Contracting and Construction is your complete solution provider for all construction needs
              across Ontario. From skilled trades in{" "}
              <Link to="/toronto" className="text-primary hover:text-primary/80 underline">
                Toronto
              </Link>{" "}
              to heavy equipment operations in{" "}
              <Link to="/ottawa" className="text-primary hover:text-primary/80 underline">
                Ottawa
              </Link>
              , we deliver professional results on time and on budget.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* General Contracting */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/20">
              <CardHeader>
                <div className="bg-primary/10 p-3 rounded-lg w-fit group-hover:bg-primary/20 transition-colors">
                  <Building className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">General Contracting</CardTitle>
                <CardDescription>
                  Complete project management from planning to completion for residential, commercial, and industrial
                  projects.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Project Management
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Site Coordination
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Quality Control
                  </li>
                </ul>
                <Link to="/services/general-contracting">
                  <Button variant="outline" className="w-full">
                    Learn More About General Contracting Services
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Skilled Trades */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/20">
              <CardHeader>
                <div className="bg-primary/10 p-3 rounded-lg w-fit group-hover:bg-primary/20 transition-colors">
                  <Hammer className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Skilled Trades</CardTitle>
                <CardDescription>
                  Expert craftsmen in electrical, plumbing, carpentry, HVAC, and all essential building trades.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Electrical Work
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Plumbing Services
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Carpentry & Millwork
                  </li>
                </ul>
                <Link to="/services/skilled-trades">
                  <Button variant="outline" className="w-full">
                    Learn More About Skilled Trades Services
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Heavy Equipment */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/20">
              <CardHeader>
                <div className="bg-primary/10 p-3 rounded-lg w-fit group-hover:bg-primary/20 transition-colors">
                  <Truck className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Heavy Equipment & Site Work</CardTitle>
                <CardDescription>
                  Excavation, earthmoving, and site preparation with our fleet of modern heavy equipment.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Excavation Services
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Site Preparation
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Earthmoving
                  </li>
                </ul>
                <Link to="/services/heavy-equipment">
                  <Button variant="outline" className="w-full">
                    Learn More About Heavy Equipment Services
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Specialty Contracting */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/20">
              <CardHeader>
                <div className="bg-primary/10 p-3 rounded-lg w-fit group-hover:bg-primary/20 transition-colors">
                  <Wrench className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Specialty Contracting</CardTitle>
                <CardDescription>
                  Specialized services including concrete work, steel fabrication, and custom construction solutions.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Concrete & Masonry
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Steel Fabrication
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Custom Solutions
                  </li>
                </ul>
                <Link to="/services/specialty-contracting">
                  <Button variant="outline" className="w-full">
                    Learn More About Specialty Contracting Services
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Design & Engineering */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/20">
              <CardHeader>
                <div className="bg-primary/10 p-3 rounded-lg w-fit group-hover:bg-primary/20 transition-colors">
                  <Zap className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Design & Engineering Support</CardTitle>
                <CardDescription>
                  Comprehensive design services and engineering support for complex construction projects.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Architectural Design
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Structural Engineering
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Project Planning
                  </li>
                </ul>
                <Link to="/services/design-engineering">
                  <Button variant="outline" className="w-full">
                    Learn More About Design & Engineering Services
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Support & Safety */}
            <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/20">
              <CardHeader>
                <div className="bg-primary/10 p-3 rounded-lg w-fit group-hover:bg-primary/20 transition-colors">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Support & Safety Services</CardTitle>
                <CardDescription>
                  Professional safety coordination, administrative support, and compliance management.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Safety Coordination
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Compliance Management
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Administrative Support
                  </li>
                </ul>
                <Link to="/services/support-safety">
                  <Button variant="outline" className="w-full">
                    Learn More About Support & Safety Services
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Ontario Service Areas */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Serving Communities Across Ontario</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Blue Knight Contracting proudly serves homeowners, developers, and businesses across Ontario, including
              major cities and surrounding communities with reliable, professional construction services.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { city: "Toronto", region: "GTA & Surrounding Areas" },
              { city: "Ottawa", region: "National Capital Region" },
              { city: "Mississauga", region: "Peel Region" },
              { city: "Hamilton", region: "Golden Horseshoe" },
              { city: "London", region: "Southwestern Ontario" },
              { city: "Kitchener-Waterloo", region: "Waterloo Region" },
              { city: "Windsor", region: "Essex County" },
              { city: "Kingston", region: "Eastern Ontario" },
            ].map((location) => (
              <Card key={location.city} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <MapPin className="h-8 w-8 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold text-lg mb-1">{location.city}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{location.region}</p>
                  <Link to={`/${location.city.toLowerCase().replace("-", "")}`}>
                    <Button variant="outline" size="sm">
                      View Services
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-muted-foreground mb-6">
              Looking for a <strong>general contractor in Toronto</strong>? Need{" "}
              <strong>construction services in Ottawa</strong>? Blue Knight Contracting provides expert{" "}
              <strong>contracting services across Ontario</strong>, including
              <strong>Mississauga</strong>, <strong>Hamilton</strong>, <strong>London</strong>,{" "}
              <strong>Kitchener</strong>, and beyond.
            </p>
            <Link to="/locations">
              <Button variant="cta" size="lg">
                View All Service Areas
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Residential, Commercial & Industrial Projects</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              From custom home builds to large-scale commercial developments, we have the expertise and equipment to
              handle projects of any size and complexity.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <Card className="overflow-hidden">
              <div className="aspect-video relative">
                <img
                  src={residentialImage}
                  alt="Residential construction and renovation services by Blue Knight Contracting in Ontario"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  width="411"
                  height="274"
                />
              </div>
              <CardHeader>
                <CardTitle className="text-xl">Residential Construction</CardTitle>
                <CardDescription>
                  Custom homes, renovations, additions, and residential repairs across Ontario.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                  <li>• Custom Home Building</li>
                  <li>
                    •{" "}
                    <Link to="/services/kitchen-renovations" className="text-primary hover:underline">
                      Kitchen Renovations
                    </Link>{" "}
                    &{" "}
                    <Link to="/services/bathroom-renovations" className="text-primary hover:underline">
                      Bathroom Renovations
                    </Link>
                  </li>
                  <li>• Home Additions & Extensions</li>
                  <li>• Basement Finishing</li>
                </ul>
                <Button variant="outline" className="w-full">
                  Residential Services
                </Button>
              </CardContent>
            </Card>

            <Card className="overflow-hidden">
              <div className="aspect-video relative">
                <img
                  src={heroImage}
                  alt="Commercial construction projects by Blue Knight Contracting across Ontario"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  width="411"
                  height="274"
                />
              </div>
              <CardHeader>
                <CardTitle className="text-xl">Commercial Construction</CardTitle>
                <CardDescription>
                  Office buildings, retail spaces, warehouses, and commercial developments.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                  <li>• Office Building Construction</li>
                  <li>• Retail & Restaurant Build-outs</li>
                  <li>• Warehouse Development</li>
                  <li>• Commercial Renovations</li>
                </ul>
                <Button variant="outline" className="w-full">
                  Commercial Services
                </Button>
              </CardContent>
            </Card>

            <Card className="overflow-hidden">
              <div className="aspect-video relative">
                <img
                  src={heavyEquipmentImage}
                  alt="Industrial construction and heavy equipment services by Blue Knight Contracting"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  width="411"
                  height="274"
                />
              </div>
              <CardHeader>
                <CardTitle className="text-xl">Industrial Construction</CardTitle>
                <CardDescription>
                  Manufacturing facilities, processing plants, and heavy industrial projects.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground mb-4">
                  <li>• Manufacturing Facilities</li>
                  <li>• Processing Plants</li>
                  <li>• Infrastructure Projects</li>
                  <li>• Heavy Equipment Installation</li>
                </ul>
                <Button variant="outline" className="w-full">
                  Industrial Services
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* AI Q&A Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Frequently Asked Questions About Ontario Construction Services
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Common questions about Blue Knight Contracting and Construction's services across Ontario.
            </p>
          </div>

          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-primary/10">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-3 text-primary">
                  Q: Who is Blue Knight Contracting and Construction?
                </h3>
                <p className="text-muted-foreground">
                  A: Blue Knight Contracting and Construction is a full-service Ontario general contractor providing
                  residential, commercial, and industrial construction services across multiple cities including
                  Toronto, Ottawa, Mississauga, Hamilton, London, Windsor, Kingston, and throughout Ontario.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-primary/10">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-3 text-primary">
                  Q: What construction services do you provide in Ontario?
                </h3>
                <p className="text-muted-foreground">
                  A: We provide comprehensive construction services including general contracting, skilled trades
                  (electricians, plumbers, carpenters, HVAC), heavy equipment and site work, specialty contracting,
                  design and engineering support, and safety coordination across all of Ontario.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-primary/10">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-3 text-primary">
                  Q: Do you offer construction services in Toronto and the GTA?
                </h3>
                <p className="text-muted-foreground">
                  A: Yes, Blue Knight Contracting provides full construction and contracting services in Toronto,
                  Mississauga, Brampton, Vaughan, Markham, Scarborough, and throughout the Greater Toronto Area (GTA)
                  for residential, commercial, and industrial projects.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-primary/10">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-3 text-primary">
                  Q: Are you licensed and insured for construction work in Ontario?
                </h3>
                <p className="text-muted-foreground">
                  A: Yes, Blue Knight Contracting and Construction is fully licensed, bonded, and insured across Ontario
                  with over 20 years of experience and comprehensive coverage for all our construction and contracting
                  services.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-primary/10">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-3 text-primary">
                  Q: Can I get a free quote for my Ontario construction project?
                </h3>
                <p className="text-muted-foreground">
                  A: Yes, Blue Knight Contracting provides free detailed quotes and consultations for all construction
                  projects across Ontario. Contact us at (416) 555-BLUE or through our website to request your free
                  quote today.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-primary/10">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-3 text-primary">Q: What areas of Ontario do you serve?</h3>
                <p className="text-muted-foreground">
                  A: Blue Knight Contracting serves all of Ontario including major cities like Toronto, Ottawa,
                  Mississauga, Hamilton, London, Windsor, Kingston, Barrie, Sudbury, Thunder Bay, and surrounding
                  communities for all types of construction and contracting services.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">Ready to Start Your Ontario Construction Project?</h2>
          <p className="text-xl opacity-90 mb-8 max-w-3xl mx-auto">
            Contact Blue Knight Contracting today for a free consultation and quote. Our experienced team is ready to
            bring your vision to life with quality craftsmanship and professional service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="hero"
              size="lg"
              className="text-lg px-8 py-4"
            >
              <Phone className="mr-2 h-5 w-5" />
              Call (647) 539-6097
            </Button>
            <Button variant="contact" size="lg" className="text-lg px-8 py-4">
              <Mail className="mr-2 h-5 w-5" />
              Request Free Quote
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
